import type { PromptSnippetChatType } from "@/schema/prompt-snippet"
import { useQuery } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

import { fetchPromptSnippets } from "./api"

export function useFetchPromptSnippets(options?: {
  enabled?: boolean
  scope?: "company" | "book"
  chatType?: PromptSnippetChatType
  includeSystem?: boolean
}) {
  const getContext = useApiContext()
  const bookId = useBookId()
  const scopeSegment = options?.scope === "company" ? "company" : (bookId ?? "company")

  const includeSystem = options?.includeSystem === true
  const queryKey = [
    "prompt-snippets",
    scopeSegment,
    options?.chatType || "all",
    includeSystem ? "with-system" : "without-system",
  ]

  return useQuery({
    queryKey,
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchPromptSnippets(context, {
        scope: options?.scope,
        chatType: options?.chatType,
        includeSystem: options?.includeSystem,
      })
    },
    enabled: options?.enabled ?? true,
    retry: false,
  })
}

export function useFetchAllPromptSnippets(options?: {
  enabled?: boolean
  chatType?: PromptSnippetChatType
  includeSystem?: boolean
  bookId?: string | null
}) {
  const urlBookId = useBookId()
  const bookId = options?.bookId || urlBookId
  const getContext = useApiContext(bookId)
  const scopeSegment = bookId ?? "all-books"
  const includeSystem = options?.includeSystem === true
  const queryKey = [
    "prompt-snippets",
    "all",
    scopeSegment,
    options?.chatType || "all",
    includeSystem ? "with-system" : "without-system",
  ]

  return useQuery({
    queryKey,
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchPromptSnippets(context, {
        chatType: options?.chatType,
        includeSystem: options?.includeSystem,
      })
    },
    enabled: options?.enabled ?? true,
    retry: false,
  })
}
